import * as formik from 'formik';
import * as fabric from 'office-ui-fabric-react';
import { FC, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import * as Yup from 'yup';
import * as api from '../../../../../api/generated';
import { KendoLoading } from '../../../../../components';
import { IWindowComponentProps } from '../../../../../components/windows/WindowContext';
import { IGroupListItem } from '../../../../../models/classes/IGroup';

interface IGroupEditModalProps {
    groupId: number;
    refetchGroups(): Promise<void>;
}

const validationSchema = Yup.object<IGroupListItem>().shape({
    name_LT: Yup.string().required('Šis laukas yra privalomas').max(50, 'Maksimalus lauko ilgis: 50 simbolių'),
    name_EN: Yup.string().required('Šis laukas yra privalomas').max(50, 'Maksimalus lauko ilgis: 50 simbolių'),
    name_RU: Yup.string().required('Šis laukas yra privalomas').max(50, 'Maksimalus lauko ilgis: 50 simbolių'),
    description_LT: Yup.string().required('Šis laukas yra privalomas').max(200, 'Maksimalus lauko ilgis: 200 simbolių'),
    description_EN: Yup.string().required('Šis laukas yra privalomas').max(200, 'Maksimalus lauko ilgis: 200 simbolių'),
    description_RU: Yup.string().required('Šis laukas yra privalomas').max(200, 'Maksimalus lauko ilgis: 200 simbolių')
});

const GroupEditModalWindow: FC<IWindowComponentProps<IGroupEditModalProps>> = (props) => {
    const {
        componentProps: { groupId, refetchGroups },
        onClose
    } = props;

    const [isLoading, setIsLoading] = useState(false);
    const [group, setGroup] = useState<IGroupListItem | null>(null);

    const fetchData = async () => {
        try {
            setIsLoading(true);
            const resp = await api.groups.getGroup(groupId);
            setGroup(resp);
        } catch (ex) {
        } finally {
            setIsLoading(false);
        }
    };

    const handleSubmit = async (values: IGroupListItem, formikActions: formik.FormikHelpers<IGroupListItem>) => {
        try {
            setIsLoading(true);
            formikActions.setStatus();
            await api.groups.updateGroup(values);
            toast.warning('Grupės informacija sėkmingai išsaugota, norint kad grupės pavadinima pasikeistų terminale, prašome jį perkrauti');

            formikActions.resetForm();
            await refetchGroups();
        } catch (ex) {
            formikActions.setStatus(ex);
            toast.error(ex.error[0]);
        } finally {
            formikActions.setSubmitting(false);
            setIsLoading(false);
            onClose();
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    return (
        <>
            {isLoading ? (
                <KendoLoading />
            ) : (
                <>
                    {group && (
                        <formik.Formik validationSchema={validationSchema} initialValues={{ ...group }} onSubmit={handleSubmit}>
                            {(formikProps) => (
                                <formik.Form>
                                    <div className="ms-Grid-row">
                                        <div className="ms-Grid-col ms-sm4 ms-md4 ms-lg4">
                                            <formik.Field
                                                name="name_LT"
                                                render={({ field, form }: formik.FieldProps<string>) => (
                                                    <fabric.TextField
                                                        label="Grupės pavadinimas LT"
                                                        {...field}
                                                        errorMessage={
                                                            (formikProps.status && formikProps.status.name_LT) ||
                                                            (form.errors.name_LT && form.errors.name_LT)
                                                        }
                                                    />
                                                )}
                                            />
                                        </div>
                                        <div className="ms-Grid-col ms-sm4 ms-md4 ms-lg4">
                                            <formik.Field
                                                name="name_EN"
                                                render={({ field, form }: formik.FieldProps<string>) => (
                                                    <fabric.TextField
                                                        label="Grupės pavadinimas EN"
                                                        {...field}
                                                        errorMessage={
                                                            (formikProps.status && formikProps.status.name_EN) ||
                                                            (form.errors.name_EN && form.errors.name_EN)
                                                        }
                                                    />
                                                )}
                                            />
                                        </div>
                                        <div className="ms-Grid-col ms-sm4 ms-md4 ms-lg4">
                                            <formik.Field
                                                name="name_RU"
                                                render={({ field, form }: formik.FieldProps<string>) => (
                                                    <fabric.TextField
                                                        label="Grupės pavadinimas RU"
                                                        {...field}
                                                        errorMessage={
                                                            (formikProps.status && formikProps.status.name_RU) ||
                                                            (form.errors.name_RU && form.errors.name_RU)
                                                        }
                                                    />
                                                )}
                                            />
                                        </div>
                                    </div>
                                    <div className="ms-Grid-row">
                                        <div className="ms-Grid-col ms-sm4 ms-md4 ms-lg4">
                                            <formik.Field
                                                name="description_LT"
                                                render={({ field, form }: formik.FieldProps<string>) => (
                                                    <fabric.TextField
                                                        label="Grupės aprašymas LT"
                                                        multiline
                                                        {...field}
                                                        errorMessage={
                                                            (formikProps.status && formikProps.status.description_LT) ||
                                                            (form.errors.description_LT && form.errors.description_LT)
                                                        }
                                                    />
                                                )}
                                            />
                                        </div>
                                        <div className="ms-Grid-col ms-sm4 ms-md4 ms-lg4">
                                            <formik.Field
                                                name="description_EN"
                                                render={({ field, form }: formik.FieldProps<string>) => (
                                                    <fabric.TextField
                                                        label="Grupės aprašymas EN"
                                                        multiline
                                                        {...field}
                                                        errorMessage={
                                                            (formikProps.status && formikProps.status.description_EN) ||
                                                            (form.errors.description_EN && form.errors.description_EN)
                                                        }
                                                    />
                                                )}
                                            />
                                        </div>
                                        <div className="ms-Grid-col ms-sm4 ms-md4 ms-lg4">
                                            <formik.Field
                                                name="description_RU"
                                                render={({ field, form }: formik.FieldProps<string>) => (
                                                    <fabric.TextField
                                                        label="Grupės aprašymas RU"
                                                        multiline
                                                        {...field}
                                                        errorMessage={
                                                            (formikProps.status && formikProps.status.description_RU) ||
                                                            (form.errors.description_RU && form.errors.description_RU)
                                                        }
                                                    />
                                                )}
                                            />
                                        </div>
                                    </div>
                                    <div style={{ textAlign: 'center', marginTop: '30px' }}>
                                        <fabric.PrimaryButton
                                            className="ms-slideRightIn40 ms-textAlignRight"
                                            text="Išsaugoti"
                                            disabled={formikProps.isSubmitting || !formikProps.isValid || !formikProps.dirty}
                                            type="submit"
                                        />
                                    </div>
                                </formik.Form>
                            )}
                        </formik.Formik>
                    )}
                </>
            )}
        </>
    );
};

export default GroupEditModalWindow;
